import {inject, Injectable} from '@angular/core';
import {ClientService} from './client.service';
import {Observable, of, Subscription, timer} from 'rxjs';
import {filter, finalize, shareReplay, switchMap, take, tap, timeout} from 'rxjs/operators';
import {ClientStateService} from './client-state.service';
import {IClientOptions} from '../../models/client-options.model';
import {environment} from '../../../environments/environment';
import {shouldMarkBetaBackendUnavailable} from './beta-backend-unavailable.util';
import {classifyShellLoadFailure, ShellLoadFailureKind} from './shell-load-failure.util';

@Injectable({
    providedIn: 'root'
})
export class ClientLoadingService {
    private clientService = inject(ClientService);
    private clientStateService = inject(ClientStateService);

    // Use the shared loading state from ClientStateService
    public clientLoading$: Observable<boolean>;

    // Cache for client loading observables
    private clientsLoadedCache: Observable<boolean> | null = null;
    private clientSelectedCache: Observable<IClientOptions> | null = null;

    // Flag to track if we're already refreshing clients
    private isRefreshingClients = false;
    private readonly clientLoadTimeoutMs = 30_000;
    private refreshSubscription: Subscription | null = null;

    // Subscription for cache reset events
    private readonly resetCacheSubscription: Subscription;

    constructor() {
        // Use the loading state from the shared service
        this.clientLoading$ = this.clientStateService.clientLoading$;

        // Subscribe to cache reset events
        this.resetCacheSubscription = this.clientStateService.resetCache$.subscribe(reset => {
            if (reset) {
                this.resetCache();
            }
        });

        // Initialize by checking if fresh client rights are already loaded.
        if (this.clientService.clients.length > 0 && this.clientService.hasFreshClientRights()) {
            this.clientStateService.setClientLoading(false, 'route-loading');
        } else {
            // Set loading state to true but don't initiate refresh yet
            // We'll wait for authentication to complete before refreshing clients
            this.clientStateService.setClientLoading(true, 'auth-session-loading');
        }
    }

    /**
     * Refreshes client data and updates loading state
     */
    public refreshClients(): void {
        // If clients and a selected client are already available, just update the loading state.
        if (this.clientService.clients.length > 0 && this.clientService.client && this.clientService.hasFreshClientRights()) {
            this.clientStateService.setBetaBackendUnavailable(false);
            this.clientStateService.clearNonTokenShellLoadFailure();
            this.clientStateService.setClientLoading(false, 'route-loading');
            return;
        }

        if (this.clientService.clients.length > 0 && !this.clientService.client && this.clientService.hasFreshClientRights()) {
            this.isRefreshingClients = true;
            this.clientStateService.setClientLoading(true, 'route-loading');
            this.clientService.handleClientSelection()
                .then(() => {
                    this.clientStateService.setBetaBackendUnavailable(false);
                    this.clientStateService.clearNonTokenShellLoadFailure();
                    this.clientStateService.setClientLoading(false, 'route-loading');
                    this.isRefreshingClients = false;
                })
                .catch((err) => {
                    this.markShellLoadFailure(err);
                    this.isRefreshingClients = false;
                });
            return;
        }

        // If we're already refreshing clients, don't start another refresh
        if (this.isRefreshingClients) {
            return;
        }

        this.isRefreshingClients = true;
        this.clientStateService.setClientLoading(true, 'route-loading');
        this.clientService.refreshClientsAsync();

        // A slow rights request is still loading, not a successful empty result.
        const checkInterval = 500; // ms
        this.refreshSubscription = timer(0, checkInterval)
            .pipe(
                filter(() => this.clientService.clients.length > 0 && this.clientService.hasFreshClientRights()),
                take(1),
                timeout(this.clientLoadTimeoutMs),
                finalize(() => { this.isRefreshingClients = false; })
            )
            .subscribe({
                next: () => {
                    this.clientStateService.setBetaBackendUnavailable(false);
                    this.clientStateService.clearNonTokenShellLoadFailure();
                    this.clientStateService.setClientLoading(false, 'route-loading');
                    this.isRefreshingClients = false;
                },
                error: error => this.markShellLoadFailure(error)
            });
    }

    /**
     * Ensures client data is loaded before proceeding
     * @param timeoutMs Maximum time to wait for client data in milliseconds
     * @returns Observable that resolves to true when client data is loaded
     */
    ensureClientsLoaded(timeoutMs: number = this.clientLoadTimeoutMs): Observable<boolean> {
        // If clients are already loaded, return immediately
        if (this.clientService.clients.length > 0 && this.clientService.hasFreshClientRights()) {
            this.clientStateService.setBetaBackendUnavailable(false);
            this.clientStateService.clearNonTokenShellLoadFailure();
            this.clientStateService.setClientLoading(false, 'route-loading');
            return of(true);
        }

        // If we already have a cached loading observable, return it
        if (this.clientsLoadedCache) {
            return this.clientsLoadedCache;
        }


        // Otherwise, refresh clients and wait for loading to complete
        this.refreshClients();

        // Create and cache the observable
        this.clientsLoadedCache = this.clientLoading$.pipe(
            filter(loading => !loading && (this.clientService.hasFreshClientRights()
                || this.clientStateService.getShellLoadFailure() !== null
                || this.clientStateService.getBetaBackendUnavailable())),
            take(1),
            timeout(timeoutMs),
            tap(() => {
                if (!this.clientService.hasFreshClientRights()) {
                    throw new Error('Fresh client rights are not loaded');
                }
            }),
            // Clear the cache when the observable completes or errors
            tap({
                complete: () => {
                    // Don't clear the cache on completion to prevent redundant loading
                },
                error: () => {
                    this.clientsLoadedCache = null;
                }
            }),
            // Share the same observable for all subscribers
            shareReplay(1)
        );

        return this.clientsLoadedCache;
    }

    /**
     * Ensures a client is selected before proceeding
     * @param timeoutMs Maximum time to wait for client selection in milliseconds
     * @returns Observable that resolves to the selected client when available
     */
    ensureClientSelected(timeoutMs: number = this.clientLoadTimeoutMs): Observable<IClientOptions> {
        // If a client is already selected, return it immediately
        if (this.clientService.client && this.clientService.hasFreshClientRights()) {
            this.clientStateService.setBetaBackendUnavailable(false);
            this.clientStateService.setClientLoading(false, 'route-loading');
            return of(this.clientService.client);
        }

        // If we already have a cached client selection observable, return it
        if (this.clientSelectedCache) {
            return this.clientSelectedCache;
        }


        // Create and cache the observable
        this.clientSelectedCache = this.ensureClientsLoaded(timeoutMs).pipe(
            switchMap(() => {
                // If a client is already selected after loading clients, return it
                if (this.clientService.client && this.clientService.hasFreshClientRights()) {
                    this.clientStateService.setBetaBackendUnavailable(false);
                    this.clientStateService.clearNonTokenShellLoadFailure();
                    this.clientStateService.setClientLoading(false, 'route-loading');
                    return of(this.clientService.client);
                }

                if (!this.clientService.hasFreshClientRights()) {
                    return new Observable<IClientOptions>(observer => {
                        observer.error(new Error('Fresh client rights are not loaded'));
                    });
                }

                // Otherwise, trigger client selection
                return new Observable<IClientOptions>(observer => {
                    this.clientService.handleClientSelection().then(() => {
                        if (this.clientService.client) {
                            this.clientStateService.setBetaBackendUnavailable(false);
                            this.clientStateService.clearNonTokenShellLoadFailure();
                            this.clientStateService.setClientLoading(false, 'route-loading');
                            observer.next(this.clientService.client);
                            observer.complete();
                        } else {
                            observer.error(new Error('No client selected after selection dialog'));
                        }
                    }).catch(err => {
                        observer.error(err);
                    });
                });
            }),
            // Clear the cache when the observable completes or errors
            tap({
                complete: () => {
                    // Don't clear the cache on completion to prevent redundant loading
                },
                error: () => {
                    this.clientSelectedCache = null;
                }
            }),
            // Share the same observable for all subscribers
            shareReplay(1)
        );

        return this.clientSelectedCache;
    }

    /**
     * Resets the client loading caches, forcing a new client load on next request
     * This should be called when a user manually selects a different client
     */
    resetCache(): void {

        this.refreshSubscription?.unsubscribe();
        this.refreshSubscription = null;

        // Clear both caches
        this.clientsLoadedCache = null;
        this.clientSelectedCache = null;

        // Reset the refreshing flag to allow a new refresh to start
        this.isRefreshingClients = false;

        // Selecting a freshly authorized client also resets dependent caches. That
        // must release callers already waiting for this same rights response.
        const ready = this.clientService.clients.length > 0 && !!this.clientService.client
            && this.clientService.hasFreshClientRights();
        this.clientStateService.setClientLoading(!ready, 'route-loading');

    }

    markBetaBackendUnavailable(error: unknown): boolean {
        if (!shouldMarkBetaBackendUnavailable(error, environment.environment)) {
            return false;
        }

        this.clientStateService.clearNonTokenShellLoadFailure();
        this.clientStateService.setBetaBackendUnavailable(true);
        this.clientStateService.setClientLoading(false, 'backend-unavailable');
        return true;
    }

    markShellLoadFailure(error: unknown): ShellLoadFailureKind | null {
        if (this.markBetaBackendUnavailable(error)) {
            return null;
        }

        const failure = classifyShellLoadFailure(error);
        this.clientStateService.setBetaBackendUnavailable(false);
        this.clientStateService.setShellLoadFailure(failure);
        this.clientStateService.setClientLoading(
            false,
            failure === 'access-denied' ? 'rbac-tenant-denial' : 'backend-unavailable'
        );
        return failure;
    }

    /**
     * Clean up subscriptions when the service is destroyed
     */
    ngOnDestroy(): void {
        this.refreshSubscription?.unsubscribe();
        if (this.resetCacheSubscription) {
            this.resetCacheSubscription.unsubscribe();
        }
    }
}
